/* eslint-disable max-lines */
import { procedureTypes } from 'lib/constants/constants';
import { inRn, mString, pString } from 'lib/helpers/utility';

import CurrencyCellEditor from 'components/shared/AGTable/components/CurrencyCellEditor';
import DiscountCellEditor from 'components/shared/AGTable/components/DiscountCellEditor';
import IncrementCellEditor from 'components/shared/AGTable/components/IncrementCellEditor';
import SelectCellEditor from 'components/shared/AGTable/components/SelectCellEditor';
import Dot from 'components/shared/Dot/Dot';
import { getValidationMessages } from 'components/validation/validate_currency_input';

// --------------------
// Constants
// --------------------
const EDITABLE_TYPES = ['primary', 'self_pay'];
const PROCEDURE_TYPES = ['primary', 'self_pay', 'unapplied'];
const EDITABLE_PRODUCT_TYPES = ['Product'];
const PRODUCT_TYPES = ['Product', 'Package'];

// --------------------
// Formatters
// --------------------
const defaultValueFormatter = (value) => mString(value || 0);

const productValueFormatter = ({ data, value }) =>
  PRODUCT_TYPES.includes(data?.type) ? mString(value) : '-';

const procedureValueFormatter = ({ data, value }) =>
  PROCEDURE_TYPES.includes(data?.type) ? defaultValueFormatter(value) : '-';

const cellDisabledClass = ({ data }) => (data?.type === 'unapplied' ? 'text-neutral-500' : '');

const withStoppedCallback = (Editor, field) => (params) => {
  const onStopped = params.context?.triggerCellStopped;
  return (
    <Editor
      {...params}
      onValueChange={(newValue) => {
        onStopped?.({
          ...params,
          value: newValue,
          data: { ...params.data, [field]: newValue }
        });
      }}
    />
  );
};

// --------------------
// Helpers
// --------------------
const renderDotValue = (value, isHighlighted) => (
  <div className="flex h-full w-full items-center gap-0.5">
    {isHighlighted && <Dot color="success" size="sm" />}
    <span className="px-2 text-sm leading-5">{mString(value)}</span>
  </div>
);

// --------------------
// Column Defs
// --------------------
export const EDIT_INVOICE_DEFAULT_COLUMN_DEFS = {
  width: 153
};

export const EDIT_INVOICE_COLUMN_DEFS = [
  {
    field: 'name',
    headerName: 'Item',
    minWidth: 200
  },
  {
    field: 'type',
    headerName: 'Type',
    width: 108,
    valueFormatter: ({ value }) => procedureTypes.find((type) => type.value === value)?.label,
    cellStyle: (params) => (EDITABLE_TYPES.includes(params.data?.type) ? { padding: 0 } : {}),
    cellRenderer: (params) =>
      [...EDITABLE_TYPES, 'unapplied'].includes(params.data?.type)
        ? withStoppedCallback(
            SelectCellEditor,
            'type'
          )({
            ...params,
            options: procedureTypes
          })
        : params.value
  },
  {
    field: 'sales_count',
    headerName: 'Qty',
    editable: ({ data }) => EDITABLE_PRODUCT_TYPES.includes(data?.type),
    valueFormatter: ({ data, value }) => (PRODUCT_TYPES.includes(data?.type) ? value : '-'),
    cellClass: cellDisabledClass,
    cellRenderer: (params) =>
      EDITABLE_PRODUCT_TYPES.includes(params?.data?.type)
        ? withStoppedCallback(IncrementCellEditor, 'sales_count')(params)
        : PRODUCT_TYPES.includes(params?.data?.type)
          ? (params?.value ?? '-')
          : '-'
  },
  {
    field: 'amount_cents',
    headerName: 'Unit Price',
    valueFormatter: productValueFormatter,
    cellClass: 'text-neutral-500'
  },
  {
    field: 'total_amount_cents',
    headerName: 'Price',
    valueFormatter: productValueFormatter,
    cellClass: 'text-neutral-500'
  },
  {
    field: 'charge',
    headerName: 'Ins. Charge',
    editable: ({ data }) => EDITABLE_TYPES.includes(data?.type),
    cellClass: cellDisabledClass,
    cellEditor: CurrencyCellEditor,
    cellEditorParams: (params) => {
      const { charge, units = 1, pt_balance, total_cost_cents } = params?.data || {};
      const value = (charge ?? pt_balance ?? total_cost_cents ?? 0) * units;
      return { value, initialValue: value };
    },
    valueFormatter: ({ data }) => {
      const { charge, units = 1, pt_balance, total_cost_cents } = data || {};
      const value = (charge ?? pt_balance ?? total_cost_cents ?? 0) * units;
      return procedureValueFormatter({ data, value });
    }
  },
  {
    field: 'pt_balance',
    headerName: 'Self pay Amt',
    editable: ({ data }) => EDITABLE_TYPES.includes(data?.type),
    cellClass: ({ data }) => {
      if (data?.type === 'self_pay') return 'text-success-600 px-0';
      if (data?.type === 'unapplied') return 'text-neutral-500';
      return '';
    },
    cellEditor: CurrencyCellEditor,
    cellEditorParams: (params) => {
      const { pt_balance, total_cost_cents, type } = params?.data || {};
      const value = pt_balance ?? total_cost_cents ?? 0;
      const isSelfPay = type === 'self_pay';
      return {
        dot: isSelfPay,
        value,
        initialValue: value,
        className: isSelfPay ? 'text-success-600' : 'px-2'
      };
    },
    cellRenderer: ({ data }) => {
      if (!PROCEDURE_TYPES.includes(data?.type)) return '-';
      return renderDotValue(
        data?.pt_balance ?? data?.total_cost_cents ?? 0,
        data?.type === 'self_pay'
      );
    }
  },
  {
    field: 'ins_payment',
    headerName: 'Allowed',
    editable: ({ data }) => EDITABLE_TYPES.includes(data?.type),
    cellClass: ({ data }) => {
      if (data?.type === 'primary') return 'text-success-600 px-0';
      if (data?.type === 'unapplied') return 'text-neutral-500';
      return '';
    },
    cellEditor: CurrencyCellEditor,
    cellEditorParams: (params) => {
      const { ins_payment, units = 1, type } = params?.data || {};
      const value = inRn(ins_payment) * units;
      const isPrimary = type === 'primary';
      return {
        dot: isPrimary,
        value,
        initialValue: value,
        className: isPrimary ? 'text-success-600' : 'px-2'
      };
    },
    cellRenderer: ({ data }) => {
      if (!PROCEDURE_TYPES.includes(data?.type)) return '-';

      return renderDotValue(
        (data?.ins_payment || 0) * (data?.units || 1),
        data?.type === 'primary'
      );
    }
  },
  {
    field: 'total_cost_cents',
    headerName: 'Est. Pt Portion',
    editable: ({ data }) => EDITABLE_TYPES.includes(data?.type),
    cellClass: cellDisabledClass,
    cellEditor: CurrencyCellEditor,
    cellEditorParams: (params) => {
      const { total_cost_cents, type, allowed, ins_payment, modified, pt_balance } =
        params?.data || {};
      let value = total_cost_cents ?? 0;
      if ((modified && pt_balance) || pt_balance > 0) value = pt_balance;
      return {
        value,
        initialValue: value,
        maxValue: type === 'primary' ? (allowed ?? ins_payment) : undefined,
        validationMessages: getValidationMessages('pt_portion')
      };
    },
    valueFormatter: ({ data }) => {
      let value = data?.total_cost_cents ?? 0;
      if ((data?.modified && data?.pt_balance) || data?.pt_balance > 0) {
        value = data.pt_balance;
      }
      return procedureValueFormatter({ data, value });
    }
  },
  {
    field: 'discount',
    headerName: 'Discount',
    valueFormatter: defaultValueFormatter,
    cellClass: cellDisabledClass,
    cellRenderer: (params) => {
      if ([...EDITABLE_TYPES, ...EDITABLE_PRODUCT_TYPES].includes(params?.data?.type))
        return withStoppedCallback(
          DiscountCellEditor,
          'discount'
        )({
          ...params,
          maxValue: inRn(params?.data?.total_cost_cents || params?.data?.total_amount_cents)
        });
      if (['unapplied', ...PRODUCT_TYPES].includes(params?.data?.type))
        return mString(params.value?.amount_cents);
      return '-';
    }
  },
  {
    field: 'local_tax_rate',
    headerName: 'Local Tax',
    editable: ({ data }) => [...EDITABLE_TYPES, ...EDITABLE_PRODUCT_TYPES].includes(data?.type),
    cellClass: cellDisabledClass,
    cellEditor: CurrencyCellEditor,
    cellEditorParams: (params) => ({
      ...params,
      style: 'percent'
    }),
    valueFormatter: ({ data }) => pString(data?.local_tax_rate ?? 0)
  },
  {
    field: 'state_tax_rate',
    headerName: 'State Tax',
    editable: ({ data }) => [...EDITABLE_TYPES, ...EDITABLE_PRODUCT_TYPES].includes(data?.type),
    cellClass: cellDisabledClass,
    cellEditor: CurrencyCellEditor,
    cellEditorParams: (params) => ({
      ...params,
      style: 'percent'
    }),
    valueFormatter: ({ data }) => pString(data?.state_tax_rate ?? 0)
  }
];
